interface Row {
  path: string;
  before?: string;
  after?: string;
  kind: "added" | "removed" | "changed" | "same";
}

function flatten(value: unknown, prefix: string, out: Map<string, string>): void {
  if (value === null || value === undefined) {
    out.set(prefix || ".", JSON.stringify(value ?? null));
    return;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      out.set(prefix || ".", "[]");
      return;
    }
    value.forEach((v, i) => flatten(v, `${prefix}[${i}]`, out));
    return;
  }
  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) {
      out.set(prefix || ".", "{}");
      return;
    }
    for (const [k, v] of entries) {
      flatten(v, prefix ? `${prefix}.${k}` : k, out);
    }
    return;
  }
  out.set(prefix || ".", JSON.stringify(value));
}

function diffRows(before: unknown, after: unknown): Row[] {
  const beforeMap = new Map<string, string>();
  const afterMap = new Map<string, string>();
  if (before !== null && before !== undefined) flatten(before, "", beforeMap);
  if (after !== null && after !== undefined) flatten(after, "", afterMap);

  const paths = new Set([...beforeMap.keys(), ...afterMap.keys()]);
  const rows: Row[] = [];
  for (const path of [...paths].sort()) {
    const b = beforeMap.get(path);
    const a = afterMap.get(path);
    if (b === undefined && a !== undefined) rows.push({ path, after: a, kind: "added" });
    else if (a === undefined && b !== undefined) rows.push({ path, before: b, kind: "removed" });
    else if (a !== b) rows.push({ path, before: b, after: a, kind: "changed" });
    else rows.push({ path, before: b, after: a, kind: "same" });
  }
  return rows;
}

/** A before/after diff for `ChangeEntry.before`/`.after` (CONTRACT §5.8), field-level rather than line-level. */
export function JsonDiff({ before, after }: { before: unknown; after: unknown }) {
  const rows = diffRows(before, after).filter((r) => r.kind !== "same");
  if (rows.length === 0) {
    return <p className="muted">No field-level differences.</p>;
  }
  return (
    <table className="table" aria-label="JSON diff">
      <thead>
        <tr>
          <th>Field</th>
          <th>Before</th>
          <th>After</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.path}>
            <td>
              <code>{r.path}</code>
            </td>
            <td className={r.kind === "removed" || r.kind === "changed" ? "diff-removed" : ""}>{r.before ?? "—"}</td>
            <td className={r.kind === "added" || r.kind === "changed" ? "diff-added" : ""}>{r.after ?? "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
